import { localize } from "@gtmhub/localization";
import { IQService, IScope, IPromise } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalService } from "angular-ui-bootstrap";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { AutomationTriggerType, IAutomation, IAutomationIndicators } from "../models/automation";
import { AutomationService } from "../services/automation";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { ICollection } from "@gtmhub/models";
import { IStateInit } from "@gtmhub/core/routing";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import { blurFocusedElement } from "@gtmhub/shared/dom";
import {
  createFilter,
  getPersistedScreenSettings,
  GridFilterDefinitions,
  GridSortingDefinitions,
  IGridFilter,
  IGridFiltersChange,
  IGridSortingRule,
  setPersistedScreenSettings,
  toRestLayerRequest,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { createRestLayerFields, IRestLayerRequest } from "@gtmhub/core";
import { automationGridFilterDefinitions, automationGridSortingDefinitions, filtersOverrideForAutomations, sortingOverrideForAutomations } from "../utils/filter-utils";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { hasFiltersWithValues } from "@gtmhub/goals/filter-utils";

const screenPath = ["automation"];

interface IAutomationListRootScopeService extends ITraceRootScopeService {
  newAutomation: {
    name?: string;
    description?: string;
  };
}

interface IAutomationListStrings {
  rules: string;
  missingItems: {
    trigger: string;
    condition: string;
    actions: string;
  };
  types: {
    description: string;
    name: string;
  };
}

export type IAutomationPropertyType = "description" | "name";

export interface IAutomationListScope extends IScope {
  // should add typization
  assigneesIdMap: IAssigneeIdMap;
  automations: ICollection<IAutomation<AutomationTriggerType>>;

  newAutomation: {
    name: string;
    description: string;
  };

  indicators: IAutomationIndicators;
  paginator: {
    currentPage: number;
    itemsPerPage: number;
    paginatorLimit: number;
    totalPages?: number;
    totalCount?: number;
  };
  filterDefinitions: GridFilterDefinitions;
  filters: IGridFilter[];
  sortingDefinitions: GridSortingDefinitions;
  sortingRules: IGridSortingRule[];
  showNoFilterResults: boolean;

  createAutomation(): void;
  deleteAutomation(automation: IAutomation<AutomationTriggerType>): void;
  editAutomation(automation: IAutomation<AutomationTriggerType>): void;
  openAutomation(automation: IAutomation<AutomationTriggerType>): void;
  openConfirmationModal(automation: IAutomation<AutomationTriggerType>): void;
  toggleAutomation(automation: IAutomation<AutomationTriggerType>): void;
  focusAutomation(event: JQueryEventObject, automation: IAutomation<AutomationTriggerType>, type: IAutomationPropertyType): void;
  cloneAutomation(id: string): void;
  onPageChange(page: number): void;
  filtersOrSortingChange(change?: IGridFiltersChange): void;
  showGridFilteringBar(): boolean;
  createTriggerValue(automation: IAutomation<AutomationTriggerType>): string;
}

const bindStateToScope = (state: IAssigneesStoreState) => ({
  assigneesIdMap: state.assignees.map,
});

export class AutomationListCtrl implements IStateInit {
  public static $inject = ["$state", "$scope", "$rootScope", "$q", "$uibModal", "AutomationService", "UIErrorHandlingService", "AssigneeActions", "$ngRedux"];

  constructor(
    private $state: StateService,
    private $scope: IAutomationListScope,
    private $rootScope: IAutomationListRootScopeService,
    private $q: IQService,
    private $uibModal: IModalService,
    private automationService: AutomationService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private $ngRedux: INgRedux
  ) {
    this.$scope.createAutomation = this.createAutomation;
    this.$scope.deleteAutomation = this.deleteAutomation;
    this.$scope.toggleAutomation = this.toggleAutomation;
    this.$scope.openAutomation = this.openAutomation;
    this.$scope.openConfirmationModal = this.openConfirmationModal;
    this.$scope.editAutomation = this.editAutomation;
    this.$scope.focusAutomation = this.focusAutomation;
    this.$scope.cloneAutomation = this.cloneAutomation;
    this.$scope.createTriggerValue = this.createTriggerValue;

    this.$scope.indicators = {
      loading: true,
      patching: false,
      creating: false,
      deleting: false,
      toggling: false,
      executing: false,
      cloning: false,
    };

    // dont delete this - for now it is needed for rules ctrl
    this.$rootScope.newAutomation = {};

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    // the wireframe has no design for paging
    // if it had then it would mess with the renaming of automations in the grid
    // (what would happen if you are on page 1, rename an automation and it goes to page 2?)
    this.$scope.paginator = {
      currentPage: 1,
      itemsPerPage: 1000,
      paginatorLimit: 5,
    };
    this.$scope.showNoFilterResults = false;

    this.$scope.onPageChange = this.onPageChange;
  }

  public stateInit(): IPromise<unknown> {
    this.initAutomations();
    this.initGridFilteringBar();
    return this.$q.resolve();
  }

  private initAutomations = (): void => {
    this.$scope.automations = { items: [], totalCount: 0 };
    this.$scope.paginator.totalPages = 0;
    this.$scope.paginator.totalCount = 0;
  };

  private automationStrings: IAutomationListStrings = {
    rules: "gtmhub.automation.rules",
    missingItems: {
      trigger: "trigger",
      condition: "condition",
      actions: "actions",
    },
    types: {
      description: "description",
      name: "name",
    },
  };

  private focusAutomation = (event: JQueryEventObject, automation: IAutomation<AutomationTriggerType>, type: IAutomationPropertyType): void => {
    event.preventDefault();
    event.stopPropagation();

    switch (type) {
      case this.automationStrings.types.description:
        setTimeout(() => document.querySelector<HTMLElement>(`#automation${automation.id}${this.automationStrings.types.description}`).focus());
        break;
      case this.automationStrings.types.name:
        setTimeout(() => document.querySelector<HTMLElement>(`#automation${automation.id}${this.automationStrings.types.name}`).focus());
        break;
    }
  };

  private enrichAutomations = (automations: IAutomation<AutomationTriggerType>[]): IAutomation<AutomationTriggerType>[] => {
    return automations.map(this.enrichAutomation);
  };

  private enrichAutomation = (automation: IAutomation<AutomationTriggerType>): IAutomation<AutomationTriggerType> => {
    // if automation has rules, actions and trigger its not in draft mode
    // therefore can be executed by user and have no missing items
    const missingItem = this.findMissingItem(automation);
    const isDraftMode = typeof missingItem === "string";
    return { ...automation, missingItem, isDraftMode };
  };

  private findMissingItem = (automation: IAutomation<AutomationTriggerType>): string | boolean => {
    if (!automation.trigger) {
      return this.automationStrings.missingItems.trigger;
    }
    let task = automation.automationTask;
    if (!task) {
      return this.automationStrings.missingItems.condition;
    }
    while (task) {
      if (!task.actions || task.actions.length === 0) {
        return this.automationStrings.missingItems.actions;
      }
      task = task.nextAutomationTask;
    }
    return false;
  };

  private openAutomation = (automation: IAutomation<AutomationTriggerType>): void => {
    this.$state.go("gtmhub.automation.rules", { automationId: automation.id });
  };

  private createTriggerValue = (automation: IAutomation<AutomationTriggerType>): string => {
    let type = "Unknown type",
      event = "Unknown event";

    switch (automation.trigger.objectType) {
      case "goal":
        type = localize("objective");
        break;
      case "metric":
        type = localize("key_result_cap");
        break;
      case "user":
        type = localize("users_cap");
        break;
    }

    switch (automation.trigger.operation) {
      case "progress changed":
        event = localize("progress_is_updated");
        break;
      case "created":
        event = localize("is_created");
        break;
      case "deleted":
        event = localize("is_deleted");
        break;
      case "modified":
        event = localize("is_edited");
        break;
      case "deactivated":
        event = localize("is_deactivated");
        break;
    }
    return type + " " + event;
  };

  private openConfirmationModal = (automation: IAutomation<AutomationTriggerType>) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/execute.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          $scope.automation = automation;
          $scope.close = () => {
            $uibModalInstance.close();
          };
          $scope.execute = () => {
            ctrl.$scope.indicators.executing = true;
            ctrl.automationService.executeAutomation(automation).then(
              () => {
                ctrl.$scope.indicators.executing = false;
                $uibModalInstance.close();
              },
              (rejection) => ctrl.handleIndicatorError(rejection, "executing")
            );
          };
        },
      ],
    });
  };

  private toggleAutomation = (automation: IAutomation<AutomationTriggerType>): void => {
    this.$scope.indicators.toggling = true;
    this.$scope.indicators[automation.id] = true;

    if (automation.status === "enabled") {
      automation.status = "disabled";
    } else if (automation.status === "disabled") {
      automation.status = "enabled";
    }

    this.$q
      .all({
        automation: this.automationService.updateAutomation(automation),
      })
      .then(
        () => {
          this.$scope.indicators.toggling = false;
          this.$scope.indicators[automation.id] = false;
        },
        (rejection) => this.handleIndicatorError(rejection, this.$scope.indicators[automation.id], true)
      );
  };

  private createAutomation = () => {
    this.$scope.indicators.creating = true;

    this.automationService
      .createAutomation({
        automation: {
          name: this.$scope.newAutomation.name,
          description: this.$scope.newAutomation.description,
        },
      })
      .then(
        (newAutomation: IAutomation<AutomationTriggerType>) => {
          this.$scope.indicators.creating = false;
          this.$state.go(this.automationStrings.rules, { automationId: newAutomation.id });
        },
        (rejection) => this.handleIndicatorError(rejection, "creating", true)
      );
  };

  private deleteAutomation = (automation: IAutomation<AutomationTriggerType>): void => {
    this.$scope.indicators.deleting = true;
    this.$scope.indicators[automation.id] = true;

    this.automationService.deleteAutomation(automation).then(
      () => {
        this.$scope.indicators[automation.id] = false;
        this.$scope.indicators.deleting = false;
        this.reloadView();
      },
      (rejection) => this.handleIndicatorError(rejection, "deleting", true)
    );
  };

  private editAutomation = (automation: IAutomation<AutomationTriggerType>): void => {
    this.automationService.updateAutomation(automation).then(
      () => {
        setTimeout(blurFocusedElement);
        this.reloadView();
      },
      (rejection) => this.uiErrorHandlingService.handleModal(rejection)
    );
  };

  private cloneAutomation = (id: string) => {
    this.$scope.indicators.cloning = true;

    this.automationService.cloneAutomation(id).then(
      (newAutomation: IAutomation<AutomationTriggerType>) => {
        this.$scope.indicators.cloning = false;
        this.openAutomation(newAutomation);
      },
      (rejection) => this.handleIndicatorError(rejection, "cloning", true)
    );
  };

  private handleIndicatorError = <R>(rejection: R, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(rejection, { indicator, inline });
  };

  private reloadView = (): void => {
    this.$state.go(".", {}, { reload: true });
  };

  private initGridFilteringBar = () => {
    this.$scope.filterDefinitions = automationGridFilterDefinitions();
    this.$scope.filters = getPersistedScreenSettings(screenPath, "filters") || [createFilter(this.$scope.filterDefinitions["name"])];

    this.$scope.sortingDefinitions = automationGridSortingDefinitions();
    this.$scope.sortingRules = getPersistedScreenSettings(screenPath, "sorting") || [{ fieldName: "name", reversed: false }];

    this.$scope.filtersOrSortingChange = this.filtersOrSortingChange;
    this.$scope.showGridFilteringBar = this.showGridFilteringBar;
  };

  private filtersOrSortingChange = (change?: IGridFiltersChange) => {
    if (!change || !change.initial) {
      setPersistedScreenSettings(screenPath, "filters", this.$scope.filters);
      setPersistedScreenSettings(screenPath, "sorting", this.$scope.sortingRules);
    }

    this.$scope.paginator.currentPage = 1;
    this.getAutomations();
  };

  private getAutomations = (): IPromise<unknown> => {
    this.$scope.indicators.loading = true;

    const request = this.createAutomationRequest();
    return this.automationService
      .getAllAutomations(request)
      .then(
        (automationCollection) => {
          this.$scope.indicators.loading = false;
          const enrichedAutomations = this.enrichAutomations(automationCollection.items);

          this.$scope.automations = { items: enrichedAutomations, totalCount: automationCollection.totalCount };
          this.$scope.paginator.totalPages = Math.ceil(automationCollection.totalCount / this.$scope.paginator.itemsPerPage);
          this.$scope.paginator.totalCount = automationCollection.totalCount;
        },
        (error) => {
          this.$scope.indicators.loading = false;
          this.initAutomations();
          this.uiErrorHandlingService.handleModal(error);
        }
      )
      .finally(() => {
        this.$scope.showNoFilterResults = this.noAutomationsFetched() && hasFiltersWithValues(this.$scope.filters);
      });
  };

  private createAutomationRequest = (): IRestLayerRequest => {
    const request = toRestLayerRequest({
      filters: this.$scope.filters,
      filtersOverride: filtersOverrideForAutomations,
      sorting: this.$scope.sortingRules,
      sortingOverride: sortingOverrideForAutomations,
    });
    request.skip = (this.$scope.paginator.currentPage - 1) * this.$scope.paginator.itemsPerPage;
    request.limit = this.$scope.paginator.itemsPerPage;
    request.fields = createRestLayerFields({ id: 1, name: 1, description: 1, status: 1, ownerId: 1, trigger: 1, automationTask: 1 });
    return request;
  };

  private onPageChange = (page: number): void => {
    this.$rootScope.traceAction("page_changed", () => {
      this.$scope.paginator.currentPage = page;
      this.getAutomations();
    });
  };

  private noAutomationsFetched = (): boolean => !this.$scope.indicators.loading && this.$scope.paginator.totalCount === 0;

  private showGridFilteringBar = (): boolean => {
    return this.$scope.showNoFilterResults || (this.$scope.automations && this.$scope.automations.totalCount > 0);
  };
}
